import * as React from 'react';
import { useDragLayer } from 'react-dnd';
import { useMutation, useQueryClient } from 'react-query';
import ImageProcessingApi from '../../../api/ImageProcessingApi';
import { IImageThumbnail } from '../../../Entities';
import { GetImagesResponse, imagesQueryKey } from '../../listImages/queries/use-query-images';

let DRAG_ITEM_TYPE = 'CustomerImageCard';

let CATEGORY_NORMAL = 1;
let CATEGORY_EXTRA = 2;

type UseImageOrderingParams = {
    images: IImageThumbnail[];
    orderId: number;
    getEffectiveCategory: (image: IImageThumbnail) => number;
};

function sortByIndex(list: IImageThumbnail[]): IImageThumbnail[] {
    return list.slice().sort((first, second) => {
        let sortIndexFirst = first.sortIndex;
        let sortIndexSecond = second.sortIndex;

        if (sortIndexFirst === 0) {
            return -1;
        }

        if (sortIndexSecond === 0) {
            return 1;
        }

        if (sortIndexFirst === null || sortIndexSecond === null) {
            return 0;
        }

        return sortIndexFirst - sortIndexSecond;
    });
}

export function useImageOrdering({
    images,
    orderId,
    getEffectiveCategory
}: UseImageOrderingParams) {
    let queryClient = useQueryClient();

    // Derive sorted lists from props — react-query cache is the source of truth
    let sortedDelivered = React.useMemo(
        function () {
            return sortByIndex(
                images.filter(function (img) {
                    return getEffectiveCategory(img) === CATEGORY_NORMAL;
                })
            );
        },
        [images, getEffectiveCategory]
    );

    let sortedExtraImages = React.useMemo(() => {
        return sortByIndex(
            images.filter(function (img) {
                return getEffectiveCategory(img) === CATEGORY_EXTRA;
            })
        );
    }, [images, getEffectiveCategory]);

    // Temporary local override only while a drag is in progress (for visual card-shifting)
    let [dragOverride, setDragOverride] = React.useState<IImageThumbnail[] | null>(null);

    // Combined list: delivered first (in drag order), then extra (in sort-index order)
    let deliveredList = dragOverride !== null ? dragOverride : sortedDelivered;
    let orderedImages = React.useMemo(() => {
        return deliveredList.concat(sortedExtraImages);
    }, [deliveredList, sortedExtraImages]);

    function moveImage(dragIndex: number, hoverIndex: number) {
        setDragOverride((prev) => {
            let source = prev !== null ? prev : sortedDelivered;
            let updated = source.slice();
            let dragged = updated.splice(dragIndex, 1)[0];
            updated.splice(hoverIndex, 0, dragged);
            return updated;
        });
    }

    let reorderMutation = useMutation(
        (sortOrder: { id: number; idx: number }[]) => {
            return ImageProcessingApi.updateImagesSortOrder(orderId, sortOrder);
        },
        {
            onMutate: async function (sortOrder) {
                await queryClient.cancelQueries([imagesQueryKey, { orderId }]);

                let previousData = queryClient.getQueryData<GetImagesResponse>([
                    imagesQueryKey,
                    { orderId }
                ]);

                let sortIndexById = new Map(
                    sortOrder.map(function (item) {
                        return [item.id, item.idx] as const;
                    })
                );

                queryClient.setQueryData<GetImagesResponse>(
                    [imagesQueryKey, { orderId }],
                    function (oldData) {
                        if (!oldData) return oldData;
                        return oldData.map((product) => {
                            return {
                                ...product,
                                thumbnails: product.thumbnails.map((thumbnail) => {
                                    let newIndex = sortIndexById.get(thumbnail.id);
                                    if (newIndex !== undefined) {
                                        return { ...thumbnail, sortIndex: newIndex };
                                    }
                                    return thumbnail;
                                })
                            };
                        });
                    }
                );

                setDragOverride(null);

                return { previousData };
            },
            onError: function (_error, _variables, context) {
                if (context && context.previousData) {
                    queryClient.setQueryData([imagesQueryKey, { orderId }], context.previousData);
                }
            },
            onSettled: function () {
                queryClient.invalidateQueries([imagesQueryKey, { orderId }]);
            }
        }
    );

    function handleDragEnd() {
        let currentDelivered = dragOverride !== null ? dragOverride : sortedDelivered;
        let sortOrder = currentDelivered.map((img, idx) => {
            return { id: img.id, idx: idx };
        });

        reorderMutation.mutate(sortOrder);
    }

    function updateImageFilename(imageId: number, newFilename: string) {
        queryClient.setQueryData<GetImagesResponse>(
            [imagesQueryKey, { orderId }],
            function (oldData) {
                if (!oldData) return oldData;
                return oldData.map((product) => {
                    return {
                        ...product,
                        thumbnails: product.thumbnails.map((thumbnail) => {
                            if (thumbnail.id === imageId) {
                                return { ...thumbnail, filename: newFilename };
                            }
                            return thumbnail;
                        })
                    };
                });
            }
        );
    }

    function isImageDraggable(image: IImageThumbnail): boolean {
        return getEffectiveCategory(image) === CATEGORY_NORMAL;
    }

    // Detect when any image card is being dragged (for drop-zone visual cues)
    let isDragActive = useDragLayer((monitor) => {
        let itemType = monitor.getItemType();
        return monitor.isDragging() && itemType === DRAG_ITEM_TYPE;
    });

    return {
        orderedImages,
        moveImage,
        handleDragEnd,
        updateImageFilename,
        isImageDraggable,
        isDragActive
    };
}
